import { FormEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { loadStripe, Stripe, StripeCardElement } from '@stripe/stripe-js';
import { Elements, CardElement, useElements, useStripe } from '@stripe/react-stripe-js';
import images from '../../assets/images';
import { AUCTION_CONFIG, getAuctionApiBase } from './auctionConfig';
import styles from './Auction.module.css';

type PublicBid = {
  name: string;
  emailMasked: string;
  amountCents: number;
  status: string;
  createdAt: string;
  isLeading?: boolean;
};

type AuctionApiState = {
  id: string;
  status: 'live' | 'closed' | 'sold' | 'unsold';
  title?: string;
  packageName?: string;
  section?: string;
  row?: string;
  qtyLabel?: string;
  venue?: string;
  eventDateLabel?: string;
  startingBidCents: number;
  reserveCents: number;
  minIncrementCents: number;
  currentBidCents: number;
  minNextBidCents: number;
  endsAtIso: string;
  softCloseExtendMinutes: number;
  bidCount: number;
  bidderCount: number;
  reserveMet: boolean;
  bids: PublicBid[];
  winner: null | {
    name: string;
    emailMasked: string;
    amountCents: number;
    emailConfirmed: boolean;
    paymentCaptured: boolean;
  };
};

type FormErrors = { name?: string; email?: string; amount?: string; card?: string; depositAck?: string };

const formatUsdFromCents = (cents: number) =>
  `$${Math.round(cents / 100).toLocaleString('en-US')}`;

const pad2 = (n: number) => String(Math.max(0, n)).padStart(2, '0');

/** Deadline line from API endsAt (DB) — show Eastern Time (auction market). */
const formatDeadlineLabel = (endsAtIso?: string | null) => {
  if (!endsAtIso) return 'Deadline loads with auction…';
  const d = new Date(endsAtIso);
  if (!Number.isFinite(d.getTime())) return 'Deadline loads with auction…';
  const eastern = d.toLocaleString('en-US', {
    timeZone: 'America/New_York',
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    timeZoneName: 'short',
  });
  return `Deadline: ${eastern}`;
};

const CARD_STYLE = {
  style: {
    base: {
      color: '#E8EAFF',
      fontFamily: 'Barlow, sans-serif',
      fontSize: '16px',
      fontWeight: '600',
      '::placeholder': { color: '#5C639A' },
      iconColor: '#8489f0',
    },
    invalid: { color: '#ef4444', iconColor: '#ef4444' },
  },
};

let stripePromise: Promise<Stripe | null> | null = null;
const getStripePromise = () => {
  if (!stripePromise) {
    const key = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || '';
    stripePromise = key ? loadStripe(key) : Promise.resolve(null);
  }
  return stripePromise;
};

function BidForm({
  auction,
  onSuccess,
  showToast,
}: {
  auction: AuctionApiState;
  onSuccess: () => void;
  showToast: (icon: string, msg: string) => void;
}) {
  const stripe = useStripe();
  const elements = useElements();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [amount, setAmount] = useState('');
  const [depositAck, setDepositAck] = useState(false);
  const [errors, setErrors] = useState<FormErrors>({});
  const [submitting, setSubmitting] = useState(false);

  const minUsd = auction.minNextBidCents / 100;
  const auctionEnded = auction.status !== 'live';
  const depositCents = Math.round((auction.reserveCents || 2500000) * 0.1);

  const validate = (): FormErrors => {
    const next: FormErrors = {};
    if (!name.trim()) next.name = 'Please enter your name';
    if (!email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) {
      next.email = 'Enter a valid email';
    }
    const amt = parseFloat(amount);
    if (!Number.isFinite(amt) || amt * 100 < auction.minNextBidCents) {
      next.amount = `Bid must be at least ${formatUsdFromCents(auction.minNextBidCents)}`;
    }
    if (!depositAck) {
      next.depositAck = 'Please confirm the 10% security deposit acknowledgment to continue';
    }
    return next;
  };

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (auctionEnded || submitting || !depositAck) return;

    const nextErrors = validate();
    if (!stripe || !elements) {
      nextErrors.card = 'Stripe is still loading — wait a moment and retry';
    }
    const card = elements?.getElement(CardElement) as StripeCardElement | null;
    if (!card) nextErrors.card = 'Enter your card details';

    setErrors(nextErrors);
    if (Object.keys(nextErrors).length) return;

    const apiBase = getAuctionApiBase();
    if (!apiBase) {
      showToast('❌', 'Auction API URL is not configured (REACT_APP_NODE_API_URL)');
      return;
    }

    const amountCents = Math.round(parseFloat(amount) * 100);
    setSubmitting(true);

    try {
      const prepareRes = await fetch(`${apiBase}/api/auction/prepare-bid`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: name.trim(),
          email: email.trim().toLowerCase(),
          amountCents,
        }),
      });
      const prepareJson = await prepareRes.json();
      if (!prepareRes.ok) {
        throw new Error(prepareJson.message || 'Could not start deposit payment');
      }

      let paymentIntentId = prepareJson.paymentIntentId as string;

      if (!prepareJson.depositAlreadyPaid) {
        const { error: stripeError, paymentIntent } = await stripe!.confirmCardPayment(
          prepareJson.clientSecret,
          {
            payment_method: {
              card: card!,
              billing_details: {
                name: name.trim(),
                email: email.trim().toLowerCase(),
              },
            },
          },
        );

        if (stripeError) {
          throw new Error(stripeError.message || 'Deposit payment failed');
        }
        if (!paymentIntent || paymentIntent.status !== 'succeeded') {
          throw new Error(`Unexpected payment status: ${paymentIntent?.status || 'unknown'}`);
        }
        paymentIntentId = paymentIntent.id;
      }

      const confirmRes = await fetch(`${apiBase}/api/auction/confirm-bid`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          paymentIntentId,
          name: name.trim(),
          email: email.trim().toLowerCase(),
          amountCents,
        }),
      });
      const confirmJson = await confirmRes.json();
      if (!confirmRes.ok) {
        onSuccess();
        throw new Error(confirmJson.message || 'Bid could not be recorded');
      }

      const depositCents =
        confirmJson.depositAmountCents ??
        prepareJson.depositAmountCents ??
        Math.round((auction.reserveCents || 2500000) * 0.1);
      setAmount('');
      card!.clear();
      setDepositAck(false);
      showToast(
        '✅',
        prepareJson.depositAlreadyPaid
          ? `Bid updated to ${formatUsdFromCents(amountCents)}. Deposit ${formatUsdFromCents(depositCents)} already on file.`
          : `Bid ${formatUsdFromCents(amountCents)} recorded. Deposit ${formatUsdFromCents(depositCents)} charged — refunded if you don’t win.`,
      );
      if (confirmJson.extended) {
        showToast('⚡', `Auction extended ${auction.softCloseExtendMinutes} minutes`);
      }
      onSuccess();
    } catch (err: any) {
      showToast('❌', err?.message || 'Bid failed');
      setErrors((prev) => ({ ...prev, card: err?.message }));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form className={styles.formBody} onSubmit={onSubmit} noValidate>
      <div className={styles.securityBox}>
        <span className={styles.icoBlue}>🔒</span>
        <span>
          A fixed <strong>10% security deposit</strong> (
          {formatUsdFromCents((auction.reserveCents || 2500000) * 0.1)}) is charged once via
          Stripe. If you don’t win, it’s <strong>refunded after close</strong>. If you win, you pay
          only the remaining balance.
        </span>
      </div>

      <div className={styles.formGroup}>
        <label htmlFor="auction-name">Full Name</label>
        <input
          id="auction-name"
          type="text"
          autoComplete="name"
          placeholder="Your full name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          className={errors.name ? styles.inputError : undefined}
          disabled={auctionEnded || submitting}
        />
        {errors.name && <div className={styles.errMsg}>{errors.name}</div>}
      </div>

      <div className={styles.formGroup}>
        <label htmlFor="auction-email">Email Address</label>
        <input
          id="auction-email"
          type="email"
          autoComplete="email"
          placeholder="you@email.com"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          className={errors.email ? styles.inputError : undefined}
          disabled={auctionEnded || submitting}
        />
        {errors.email && <div className={styles.errMsg}>{errors.email}</div>}
      </div>

      <div className={styles.formGroup}>
        <label htmlFor="auction-amount">Your Bid</label>
        <div className={styles.amountWrap}>
          <span className={styles.amountPrefix}>$</span>
          <input
            id="auction-amount"
            type="number"
            inputMode="decimal"
            placeholder={String(minUsd)}
            min={minUsd}
            step="any"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            className={errors.amount ? styles.inputError : undefined}
            disabled={auctionEnded || submitting}
          />
          <div className={styles.amountSteppers} aria-hidden={auctionEnded || submitting}>
            <button
              type="button"
              className={styles.amountStepBtn}
              disabled={auctionEnded || submitting}
              onClick={() => {
                const inc = auction.minIncrementCents / 100;
                const cur = parseFloat(amount);
                const base = Number.isFinite(cur) ? cur : minUsd;
                setAmount(String(Math.max(minUsd, Math.round((base + inc) * 100) / 100)));
              }}
              aria-label={`Increase bid by $${auction.minIncrementCents / 100}`}
            >
              ▲
            </button>
            <button
              type="button"
              className={styles.amountStepBtn}
              disabled={auctionEnded || submitting}
              onClick={() => {
                const inc = auction.minIncrementCents / 100;
                const cur = parseFloat(amount);
                const base = Number.isFinite(cur) ? cur : minUsd;
                setAmount(String(Math.max(minUsd, Math.round((base - inc) * 100) / 100)));
              }}
              aria-label={`Decrease bid by $${auction.minIncrementCents / 100}`}
            >
              ▼
            </button>
          </div>
        </div>
        <div className={styles.minHint}>
          Min bid: <strong>{formatUsdFromCents(auction.minNextBidCents)}</strong>
          <span className={styles.incHint}>
            {' '}
            · type any amount · steps of ${auction.minIncrementCents / 100}
          </span>
        </div>
        {errors.amount && <div className={styles.errMsg}>{errors.amount}</div>}
      </div>

      <div className={styles.formGroup}>
        <label>Card Details</label>
        <div className={styles.cardBrands}>
          <div className={`${styles.cardBrand} ${styles.visaB}`}>VISA</div>
          <div className={`${styles.cardBrand} ${styles.mcB}`}>MC</div>
          <div className={`${styles.cardBrand} ${styles.amexB}`}>AMEX</div>
        </div>
        <div className={styles.stripeCardBox}>
          <CardElement options={CARD_STYLE} />
        </div>
        {errors.card && <div className={styles.errMsg}>{errors.card}</div>}
      </div>

      <div className={styles.extendBox}>
        <span className={styles.icoGold}>⚡</span>
        <span>
          Bid in the last {auction.softCloseExtendMinutes} min? Auction auto-extends{' '}
          {auction.softCloseExtendMinutes} minutes so no one gets sniped.
        </span>
      </div>

      <label className={styles.depositAck}>
        <input
          type="checkbox"
          checked={depositAck}
          onChange={(e) => {
            setDepositAck(e.target.checked);
            if (e.target.checked) {
              setErrors((prev) => {
                const next = { ...prev };
                delete next.depositAck;
                return next;
              });
            }
          }}
          disabled={auctionEnded || submitting}
        />
        <span>
          I agree that a <strong>10% security deposit</strong> of{' '}
          <strong>{formatUsdFromCents(depositCents)}</strong> will be charged now. If I do not win
          the auction, the deposit will be refunded after the auction closes. If I win, the remaining
          balance of my winning bid will be charged.
        </span>
      </label>
      {errors.depositAck && <div className={styles.errMsg}>{errors.depositAck}</div>}

      <button
        className={styles.btnBid}
        type="submit"
        disabled={auctionEnded || submitting || !stripe || !depositAck}
      >
        {auctionEnded
          ? 'AUCTION ENDED'
          : submitting
            ? 'Processing…'
            : !depositAck
              ? 'CHECK DEPOSIT BOX TO CONTINUE'
              : 'PLACE BID & PAY DEPOSIT'}
      </button>

      <div className={styles.holdNote}>
        Payment runs only after you check the box above. Losers are refunded after close. Ticket
        transfer is completed manually via the FWC2026 Mobile Tickets App after winner email
        confirmation.
      </div>
    </form>
  );
}

const Auction = () => {
  const logoSrc = images.sidebarQuickAsystLogo;
  const [auction, setAuction] = useState<AuctionApiState | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [nowMs, setNowMs] = useState(() => Date.now());
  const [bidBump, setBidBump] = useState(false);
  const [toast, setToast] = useState<{ icon: string; msg: string } | null>(null);
  const [showWinner, setShowWinner] = useState(false);
  const toastTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const prevBid = useRef<number | null>(null);

  const showToast = useCallback((icon: string, msg: string) => {
    setToast({ icon, msg });
    if (toastTimer.current) clearTimeout(toastTimer.current);
    toastTimer.current = setTimeout(() => setToast(null), 4200);
  }, []);

  const fetchAuction = useCallback(async () => {
    const apiBase = getAuctionApiBase();
    if (!apiBase) {
      setLoadError('REACT_APP_NODE_API_URL is not set');
      return;
    }
    try {
      const res = await fetch(`${apiBase}/api/auction`);
      const json = await res.json();
      if (!res.ok) throw new Error(json.message || 'Failed to load auction');
      setAuction(json);
      setLoadError(null);
      if (prevBid.current != null && json.currentBidCents !== prevBid.current) {
        setBidBump(false);
        requestAnimationFrame(() => setBidBump(true));
      }
      prevBid.current = json.currentBidCents;
      if (json.status === 'sold' && json.winner) {
        setShowWinner(true);
      }
    } catch (err: any) {
      setLoadError(err?.message || 'Could not reach auction API');
    }
  }, []);

  useEffect(() => {
    fetchAuction();
    const poll = setInterval(fetchAuction, 5000);
    const tick = setInterval(() => setNowMs(Date.now()), 1000);
    return () => {
      clearInterval(poll);
      clearInterval(tick);
      if (toastTimer.current) clearTimeout(toastTimer.current);
    };
  }, [fetchAuction]);

  const endsAtMs = useMemo(() => {
    if (!auction?.endsAtIso) return Number.NaN;
    return new Date(auction.endsAtIso).getTime();
  }, [auction?.endsAtIso]);
  const remainingSec = Number.isFinite(endsAtMs)
    ? Math.max(0, Math.floor((endsAtMs - nowMs) / 1000))
    : 0;
  const hours = Math.floor(remainingSec / 3600);
  const mins = Math.floor((remainingSec % 3600) / 60);
  const secs = remainingSec % 60;
  const urgent = remainingSec > 0 && remainingSec < 300;
  const auctionEnded = auction ? auction.status !== 'live' : remainingSec <= 0;

  const currentCents = auction?.currentBidCents ?? AUCTION_CONFIG.startingBidUsd * 100;
  const reserveCents = auction?.reserveCents ?? AUCTION_CONFIG.reserveUsd * 100;
  const packageName = auction?.packageName || AUCTION_CONFIG.packageName;
  const section = auction?.section || AUCTION_CONFIG.section;
  const row = auction?.row || AUCTION_CONFIG.row;
  const qtyLabel = auction?.qtyLabel || AUCTION_CONFIG.qtyLabel;
  const venue = auction?.venue || AUCTION_CONFIG.venue;
  const eventDateLabel = auction?.eventDateLabel || AUCTION_CONFIG.eventDateLabel;
  const deadlineLabel = formatDeadlineLabel(auction?.endsAtIso);

  const steps: { label: string; state: 'done' | 'active' | 'pending'; icon: string }[] =
    auction?.status === 'sold'
      ? [
          { label: 'Ticket Verified', state: 'done', icon: '✓' },
          { label: 'Auction Live', state: 'done', icon: '✓' },
          { label: 'Bidders Active', state: 'done', icon: '✓' },
          { label: 'Deadline Hits', state: 'done', icon: '✓' },
          { label: 'Winner Identified', state: 'done', icon: '✓' },
          { label: 'Payment Captured', state: 'done', icon: '✓' },
        ]
      : auctionEnded
        ? [
            { label: 'Ticket Verified', state: 'done', icon: '✓' },
            { label: 'Auction Live', state: 'done', icon: '✓' },
            { label: 'Bidders Active', state: 'done', icon: '✓' },
            { label: 'Deadline Hits', state: 'done', icon: '✓' },
            { label: 'Winner Identified', state: 'pending', icon: '5' },
            { label: 'Payment Captured', state: 'pending', icon: '6' },
          ]
        : [
            { label: 'Ticket Verified', state: 'done', icon: '✓' },
            { label: 'Auction Live', state: 'done', icon: '✓' },
            { label: 'Bidders Active', state: 'active', icon: '⚡' },
            { label: 'Deadline Hits', state: 'pending', icon: '4' },
            { label: 'Winner Identified', state: 'pending', icon: '5' },
            { label: 'Payment Captured', state: 'pending', icon: '6' },
          ];

  const stepClass = (key: 'done' | 'active' | 'pending') =>
    key === 'done' ? styles.stepDone : key === 'active' ? styles.stepActive : styles.stepPending;

  const stripePk = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || '';

  return (
    <div className={styles.page}>
      <header className={styles.header}>
        <a className={styles.logoLink} href="https://quickasyst.com" target="_blank" rel="noreferrer">
          <img className={styles.logoImg} src={logoSrc} alt="QuickAsyst" data-testid="auction-qa-logo" />
        </a>
        <div className={styles.headerRight}>
          <div className={styles.headerHighBid} aria-label="Current highest bid">
            <span className={styles.headerHighBidLbl}>Highest bid</span>
            <span className={`${styles.headerHighBidAmt} ${bidBump ? styles.headerHighBidBump : ''}`}>
              {formatUsdFromCents(currentCents)}
            </span>
          </div>
          <div className={styles.verifiedChip}>✓ &nbsp;Ticket Verified</div>
          <div className={styles.liveBadge}>
            <div className={styles.liveDot} />
            <span className={styles.liveBadgeFull}>
              {auctionEnded ? (auction?.status === 'sold' ? 'SOLD' : 'ENDED') : 'LIVE AUCTION'}
            </span>
            <span className={styles.liveBadgeShort}>
              {auctionEnded ? (auction?.status === 'sold' ? 'SOLD' : 'ENDED') : 'LIVE'}
            </span>
          </div>
        </div>
      </header>

      <div className={styles.hero}>
        <div className={styles.heroInner}>
          <div>
            <div className={styles.heroEyebrow}>{AUCTION_CONFIG.eventEyebrow}</div>
            <h1>
              World Cup
              <br />
              Final Ticket
              <br />
              <span className={styles.highlight}>Live Auction.</span>
            </h1>
            <div className={styles.heroSub}>
              <span>{venue}</span>
              <span>{eventDateLabel}</span>
              <span>{AUCTION_CONFIG.matchup}</span>
            </div>
            <div className={styles.ticketChips}>
              <div className={styles.tchip}>
                <span className={styles.tl}>Package</span>
                <span className={styles.tv}>{packageName}</span>
              </div>
              <div className={styles.tchip}>
                <span className={styles.tl}>Section</span>
                <span className={styles.tv}>{section}</span>
              </div>
              <div className={styles.tchip}>
                <span className={styles.tl}>Row</span>
                <span className={styles.tv}>{row}</span>
              </div>
              <div className={styles.tchip}>
                <span className={styles.tl}>Qty</span>
                <span className={styles.tv}>{qtyLabel}</span>
              </div>
              <div className={`${styles.tchip} ${styles.tchipVerified}`}>
                <span className={styles.tl}>Status</span>
                <span className={styles.tv}>✓ Verified</span>
              </div>
            </div>
            <p className={styles.oneTicketNote}>
              *Please note this auction is for one ticket
            </p>
          </div>

          <div className={styles.countdownWrap}>
            <div className={styles.cdHighBid} aria-label="Current highest bid">
              <div className={styles.cdHighBidLbl}>Current highest bid</div>
              <div className={`${styles.cdHighBidAmt} ${bidBump ? styles.headerHighBidBump : ''}`}>
                {formatUsdFromCents(currentCents)}
              </div>
            </div>
            <div className={styles.cdLabel}>Auction Ends In</div>
            <div className={styles.cdDigits}>
              <div className={styles.cdUnit}>
                <span className={`${styles.cdNum} ${urgent ? styles.cdNumUrgent : ''}`}>{pad2(hours)}</span>
                <div className={styles.cdLbl}>Hrs</div>
              </div>
              <div className={styles.cdSep}>:</div>
              <div className={styles.cdUnit}>
                <span className={`${styles.cdNum} ${urgent ? styles.cdNumUrgent : ''}`}>{pad2(mins)}</span>
                <div className={styles.cdLbl}>Min</div>
              </div>
              <div className={styles.cdSep}>:</div>
              <div className={styles.cdUnit}>
                <span className={`${styles.cdNum} ${urgent ? styles.cdNumUrgent : ''}`}>{pad2(secs)}</span>
                <div className={styles.cdLbl}>Sec</div>
              </div>
            </div>
            <div className={styles.cdDeadline}>{deadlineLabel}</div>
          </div>
        </div>
      </div>

      <div className={styles.stepsWrap}>
        <div className={styles.stepsCard}>
          {steps.map((step, idx) => (
            <div key={step.label} className={`${styles.step} ${stepClass(step.state)}`}>
              <div className={styles.stepInner}>
                <div className={styles.stepIcon}>{step.icon}</div>
                <div className={styles.stepText}>{step.label}</div>
              </div>
              {idx < steps.length - 1 && <div className={styles.stepArrow}>›</div>}
            </div>
          ))}
        </div>
      </div>

      <div className={styles.experienceWrap}>
        <div className={styles.experienceCard}>
          <div className={styles.experienceEyebrow}>What's included</div>
          <h2 className={styles.experienceTitle}>{AUCTION_CONFIG.experienceTitle}</h2>
          <p className={styles.experienceIntro}>{AUCTION_CONFIG.experienceIntro}</p>
          <ul className={styles.inclusionList}>
            {AUCTION_CONFIG.inclusions.map((item) => (
              <li key={item.title} className={styles.inclusionItem}>
                <div className={styles.inclusionTitle}>{item.title}</div>
                <div className={styles.inclusionDetail}>{item.detail}</div>
              </li>
            ))}
          </ul>
        </div>
      </div>

      <div className={styles.main}>
        <div className={styles.leftCol}>
          <div className={styles.card}>
            <div className={styles.bidMeter}>
              <div className={styles.bidLeft}>
                <div className={styles.bidLbl}>Current Highest Bid</div>
                <div className={`${styles.bidAmount} ${bidBump ? styles.bidAmountBump : ''}`}>
                  {formatUsdFromCents(currentCents)}
                </div>
                <div className={styles.bidMeta}>
                  <div className={styles.bidMetaItem}>
                    <div className={styles.val}>{auction?.bidCount ?? 0}</div>
                    <div className={styles.lbl}>Bids</div>
                  </div>
                  <div className={styles.bidMetaItem}>
                    <div className={styles.val}>{auction?.bidderCount ?? 0}</div>
                    <div className={styles.lbl}>Bidders</div>
                  </div>
                </div>
              </div>
              <div className={styles.reserveCol}>
                <div className={styles.bidLbl}>Reserve</div>
                <div className={styles.reserveAmt}>{formatUsdFromCents(reserveCents)}</div>
                {auction?.reserveMet ? (
                  <div className={styles.reserveMet}>✓ Reserve Met</div>
                ) : (
                  <div className={styles.reservePending}>Reserve pending</div>
                )}
              </div>
            </div>
          </div>
        </div>

        <div>
          <div className={`${styles.card} ${styles.bidFormCard}`}>
            <div className={styles.cardHeader}>
              <div className={styles.cardTitle}>Place Your Bid</div>
              <div className={`${styles.cardBadge} ${styles.cardBadgeGreen}`}>🔒 Stripe Secure</div>
            </div>
            {!stripePk ? (
              <div className={styles.formBody}>
                <div className={styles.errMsg}>
                  Missing REACT_APP_STRIPE_PUBLISHABLE_KEY — cannot accept card bids.
                </div>
              </div>
            ) : auction ? (
              <Elements stripe={getStripePromise()}>
                <BidForm auction={auction} onSuccess={fetchAuction} showToast={showToast} />
              </Elements>
            ) : (
              <div className={styles.formBody}>
                {loadError ? (
                  <div className={styles.errMsg}>{loadError}</div>
                ) : (
                  <div className={styles.minHint}>Loading auction…</div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      <div
        className={`${styles.winnerOverlay} ${showWinner && auction?.winner ? styles.winnerOverlayShow : ''}`}
        role="dialog"
        aria-modal="true"
      >
        <div className={styles.winnerCard}>
          <div className={styles.winnerTrophy}>🏆</div>
          <div className={styles.winnerTitle}>Auction Closed</div>
          <div className={styles.winnerName}>{auction?.winner?.name || '—'}</div>
          <div className={styles.winnerEmail}>
            {auction?.winner ? `Winning contact: ${auction.winner.emailMasked}` : '—'}
          </div>
          <div className={styles.winnerAmount}>
            {auction?.winner ? formatUsdFromCents(auction.winner.amountCents) : '—'}
          </div>
          <div className={styles.winnerAmountLbl}>
            {auction?.winner?.paymentCaptured
              ? 'Winning Bid · Payment Captured'
              : 'Winning Bid'}
          </div>
          <div className={styles.winnerSteps}>
            <div className={styles.wstep}>
              <div className={styles.num}>1</div>
              <div>Payment captured from winner card via Stripe.</div>
            </div>
            <div className={styles.wstep}>
              <div className={styles.num}>2</div>
              <div>QuickAsyst confirms winner email with the buyer.</div>
            </div>
            <div className={styles.wstep}>
              <div className={styles.num}>3</div>
              <div>Sales notifies seller → FWC2026 Mobile Tickets App transfer (manual).</div>
            </div>
            <div className={styles.wstep}>
              <div className={styles.num}>4</div>
              <div>Winner accepts in the FWC2026 Mobile Tickets App and shows ticket at the gate.</div>
            </div>
          </div>
          <button type="button" className={styles.btnWinnerClose} onClick={() => setShowWinner(false)}>
            Close
          </button>
        </div>
      </div>

      <div className={`${styles.toast} ${toast ? styles.toastShow : ''}`}>
        <span>{toast?.icon}</span>
        <span>{toast?.msg}</span>
      </div>
    </div>
  );
};

export default Auction;
